// src/components/PerformanceReport.js
import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebaseConfig';
import { collection, getDocs } from "firebase/firestore";
import DonutChart from "./DonutChart";

// Helper: Checks answer with numeric tolerance
function isAnswerCorrect(studentInput, dbAnswerString) {
  if (!studentInput || !dbAnswerString) return false;

  const sStr = studentInput.toString().trim().toUpperCase();
  const options = dbAnswerString.split(',').map(s => s.trim().toUpperCase());

  // 1. Exact String Match
  if (options.includes(sStr)) return true;

  // 2. Numeric Check
  const parseVal = (val) => {
    if (val.includes('/')) {
      const [n, d] = val.split('/');
      return parseFloat(n) / parseFloat(d);
    }
    return parseFloat(val);
  };

  const sNum = parseVal(sStr);
  if (isNaN(sNum)) return false;

  return options.some((opt) => {
    const optNum = parseVal(opt);
    return !isNaN(optNum) && Math.abs(sNum - optNum) < 0.01;
  });
}

const styles = {
  loading: { padding: 12, color: '#6b7280' },
  empty: { padding: 12, color: '#6b7280' },

  sectionTitle: { fontSize: 14, fontWeight: 800, color: '#111827', margin: '14px 0 10px' },

  overallLayout: { display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 },
  scaledBlock: { border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fff' },
  scaledHeader: { fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 },
  scaledRow: { display: 'flex', gap: 18, flexWrap: 'wrap' },
  disclaimer: { fontSize: 11, color: '#6b7280', marginTop: 10, lineHeight: 1.4 },

  scoreCardStack: { display: 'flex', flexDirection: 'column', gap: 10 },
  scoreCard: { border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fff' },
  scoreCardTitle: { fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 8 },
  scoreRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  scoreStats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, color: '#374151' },

  pills: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  pill: { fontSize: 12, padding: '4px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 },

  difficultyCardWrapper: { marginTop: 12 },
  difficultyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 },
  difficultyCard: { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 12 },
  difficultyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 },
  difficultyName: { fontSize: 13, fontWeight: 900, color: '#111827' },
  difficultyTotal: { fontSize: 12, color: '#6b7280' },
  difficultyBody: { display: 'flex', gap: 12, alignItems: 'center' },
  legendList: { margin: 0, paddingLeft: 16, fontSize: 12, color: '#374151', lineHeight: 1.5 },

  sectionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 },
  sectionCard: { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 12 },
};

const formatPercent = (v) => `${(Number(v) || 0).toFixed(2)}%`;
const formatModuleName = (name) => String(name || 'Unknown').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

// Normalize difficulty to exactly Easy/Medium/Hard
const normalizeDifficulty = (d) => {
  const s = String(d || "").trim().toLowerCase();
  if (s === "easy") return "Easy";
  if (s === "medium") return "Medium";
  if (s === "hard" || s === "difficult") return "Hard";
  return null;
};

// Section Analysis ordering: follows actual test flow
// RW Routing -> RW Module A/B -> Math Routing -> Math Module A/B
const getModuleOrder = (key) => {
  const k = String(key || "").toLowerCase().replace(/[\s_-]+/g, "");

  if (k === "rwrouting" || k === "englishroutingmodule" || k === "readingwritingroutingmodule") return 1;
  if (k === "rwmodulea" || k === "englishmodulea" || k === "readingwritingmodulea") return 2;
  if (k === "rwmoduleb" || k === "englishmoduleb" || k === "readingwritingmoduleb") return 3;
  if (k === "mathrouting" || k === "mathroutingmodule") return 4;
  if (k === "mathmodulea") return 5;
  if (k === "mathmoduleb") return 6;
  return 99;
};

// Default score config matches SAT (400–1600, sections 200–800).
// Pass a different config for PSAT: { totalMax: 1520, totalRange: "320–1520", sectionMax: 760, sectionRange: "160–760" }
const DEFAULT_SCORE_CONFIG = { totalMax: 1600, totalRange: "400–1600", sectionMax: 800, sectionRange: "200–800" };

export default function PerformanceReport({ studentId, testId, summary, diagnosticCollection = "sat_diagnostic_tests", scoreConfig = null }) {
  const sc = scoreConfig || DEFAULT_SCORE_CONFIG;
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId || !testId || !summary) {
      setReportData(null);
      setLoading(false);
      return;
    }

    const fetchReportData = async () => {
      setLoading(true);
      try {
        // 1) Fetch question metadata
        const qSnapshot = await getDocs(collection(db, diagnosticCollection, testId, "questions"));
        const questionDataMap = new Map();
        qSnapshot.forEach(d => {
          const data = d.data() || {};
          const key = data.questionId || d.id; // ✅ safer
          questionDataMap.set(key, data);
        });

        // 2) Answers are passed in summary.answers
        const studentAnswers = summary.answers || {};

        const categories = {};
        const overall = {
          totalQuestions: 0,
          correct: 0,
          incorrect: 0,
          skipped: 0,
        };

        // ✅ Always have all 3 buckets, and FIX total counting
        const difficultyBuckets = {
          Easy: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
          Medium: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
          Hard: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
        };

        const moduleStatsMap = {};

        for (const [qId, ansRaw] of Object.entries(studentAnswers)) {
          const qData = questionDataMap.get(qId);
          if (!qData) continue;

          const moduleKey = qData.module || 'Unknown';

          if (!moduleStatsMap[moduleKey]) {
            moduleStatsMap[moduleKey] = {
              moduleName: moduleKey,
              totalQuestions: 0,
              correct: 0,
              incorrect: 0,
              skipped: 0,
            };
          }

          const category = qData.category || 'Uncategorized';
          if (!categories[category]) categories[category] = { correct: 0, total: 0 };

          const difficulty = normalizeDifficulty(qData.difficulty);

          const studentAns = String(ansRaw || '').trim().toUpperCase();
          const isSkipped = !studentAns;
          
          // USE NEW HELPER
          const isCorrect = !isSkipped && isAnswerCorrect(studentAns, qData.correctAnswer);

          // Totals
          overall.totalQuestions++;
          moduleStatsMap[moduleKey].totalQuestions++;
          categories[category].total++;

          // ✅ FIX: count total per difficulty
          if (difficulty) difficultyBuckets[difficulty].total++;

          if (isSkipped) {
            overall.skipped++;
            moduleStatsMap[moduleKey].skipped++;
            if (difficulty) difficultyBuckets[difficulty].skipped++;
          } else if (isCorrect) {
            overall.correct++;
            moduleStatsMap[moduleKey].correct++;
            categories[category].correct++;
            if (difficulty) difficultyBuckets[difficulty].correct++;
          } else {
            overall.incorrect++;
            moduleStatsMap[moduleKey].incorrect++;
            if (difficulty) difficultyBuckets[difficulty].incorrect++;
          }
        }

        const difficultyBreakdown = ["Easy", "Medium", "Hard"].map(name => ({ name, ...difficultyBuckets[name] }));

        const modules = Object.values(moduleStatsMap)
          .sort((a, b) => {
            const oa = getModuleOrder(a.moduleName);
            const ob = getModuleOrder(b.moduleName);
            return oa - ob || String(a.moduleName || "").localeCompare(String(b.moduleName || ""));
          })
          .map(m => ({
            name: formatModuleName(m.moduleName),
            totalQuestions: m.totalQuestions,
            correct: m.correct,
            incorrect: m.incorrect,
            skipped: m.skipped,
            accuracy: m.totalQuestions > 0 ? (m.correct / m.totalQuestions) * 100 : 0,
          }));

        setReportData({
          categories,
          overall,
          difficultyBreakdown,
          modules,
        });
      } catch (err) {
        console.error("Error building performance report:", err);
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [studentId, testId, summary, diagnosticCollection]);

  if (loading) return <div style={styles.loading}>Loading performance data...</div>;
  if (!reportData) return <div style={styles.empty}>No performance data found.</div>;

  const { overall, difficultyBreakdown, modules } = reportData;

  const totalScore = summary.totalScore ?? summary.totalScaled ?? summary.totalScaledScore ?? null;
  const rwScore = summary.rwScaledScore ?? summary.rwScaled ?? null;
  const mathScore = summary.mathScaledScore ?? summary.mathScaled ?? null;

  const accuracy = overall.totalQuestions > 0 ? (overall.correct / overall.totalQuestions) * 100 : 0;

  return (
    <div>
      <h4 style={styles.sectionTitle}>Overall Summary</h4>
      <div style={styles.overallLayout}>
        <div style={styles.scaledBlock}>
          <div style={styles.scaledHeader}>Scaled Score</div>
          <div style={styles.scaledRow}>
            <DonutChart value={totalScore} max={sc.totalMax} label="Total Score" subLabel={sc.totalRange} valueFormatter={(v) => v ?? '—'} accentColor="#2563eb" />
            <DonutChart value={rwScore} max={sc.sectionMax} label="Reading & Writing" subLabel={sc.sectionRange} valueFormatter={(v) => v ?? '—'} accentColor="#2563eb" />
            <DonutChart value={mathScore} max={sc.sectionMax} label="Math" subLabel={sc.sectionRange} valueFormatter={(v) => v ?? '—'} accentColor="#2563eb" />
          </div>
          <p style={styles.disclaimer}>
            <strong>Disclaimer:</strong> The scaled scores shown here are simulated for practice and may differ from official exam scores.
          </p>
        </div>

        <div style={styles.scoreCardStack}>
          <div style={styles.scoreCard}>
            <div style={styles.scoreCardTitle}>Your Score</div>
            <div style={styles.scoreRow}>
              <DonutChart value={accuracy} max={100} label="Accuracy" valueFormatter={(v) => formatPercent(v)} />
              <div style={styles.scoreStats}>
                <div>Total Questions: <strong>{overall.totalQuestions}</strong></div>
                <div>Total Correct: <strong>{overall.correct}</strong></div>
              </div>
            </div>
          </div>

          <div style={styles.scoreCard}>
            <div style={styles.scoreCardTitle}>Your Score Breakdown</div>
            <div style={styles.pills}>
              <div style={{ ...styles.pill, borderColor: '#86efac' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: '#22c55e', display: 'inline-block' }} /> Correct: {overall.correct}
              </div>
              <div style={{ ...styles.pill, borderColor: '#fdba74' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: '#f97316', display: 'inline-block' }} /> Incorrect: {overall.incorrect}
              </div>
              <div style={{ ...styles.pill, borderColor: '#93c5fd' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: '#60a5fa', display: 'inline-block' }} /> Skipped: {overall.skipped}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Difficulty Level Breakdown */}
      <div style={styles.difficultyCardWrapper}>
        <h4 style={styles.sectionTitle}>Difficulty Level Breakdown</h4>
        <div style={styles.difficultyGrid}>
          {difficultyBreakdown.map(diff => {
            const acc = diff.total > 0 ? (diff.correct / diff.total) * 100 : 0;
            return (
              <div key={diff.name} style={styles.difficultyCard}>
                <div style={styles.difficultyHeader}>
                  <div style={styles.difficultyName}>{diff.name}</div>
                  <div style={styles.difficultyTotal}>Total: {diff.total}</div>
                </div>
                <div style={styles.difficultyBody}>
                  <DonutChart value={acc} max={100} label="Accuracy" valueFormatter={(v) => formatPercent(v)} />
                  <ul style={styles.legendList}>
                    <li>Correct: {diff.correct}</li>
                    <li>Incorrect: {diff.incorrect}</li>
                    <li>Skipped: {diff.skipped}</li>
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section Analysis */}
      <h4 style={styles.sectionTitle}>Section Analysis</h4>
      <div style={styles.sectionGrid}>
        {modules.map(m => (
          <div key={m.name} style={styles.sectionCard}>
            <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', marginBottom: 8 }}>{m.name}</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <DonutChart value={m.accuracy} max={100} label="Accuracy" valueFormatter={(v) => formatPercent(v)} />
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                <div>Total: <strong>{m.totalQuestions}</strong></div>
                <div>Correct: <strong>{m.correct}</strong></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
